import React, { forwardRef, useCallback } from 'react';
import { Box, Typography, Chip } from '@mui/material';
import { CloudUpload as CloudUploadIcon, CheckCircle as CheckCircleIcon } from '@mui/icons-material';
// components
import { Alert } from 'components/common/Alert';
import { formatFileSize, validateFile } from 'lib/helper';
// interfaces
import { FileUploadAreaProps } from 'interfaces';
// constants
import { UPLOAD_CONFIG, UPLOAD_TEXT } from 'constants/index';
// styles
import { StyledUploadFileBox } from 'styles/commonComponentStyle';

export const FileUploadArea = forwardRef<HTMLInputElement, FileUploadAreaProps>(
  ({ selectedFile, dragActive, error, loading, onFileSelect, onDragStateChange }, ref) => {
    const handleFileSelect = async (file: File) => {
      const validationError = validateFile(file);
      if (validationError) {
        Alert.error(validationError.message);
        return;
      }

      onFileSelect(file);
    };

    const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      onDragStateChange(false);

      const files = Array.from(e.dataTransfer.files);
      if (files.length > 0) {
        await handleFileSelect(files[0]);
      }
    };

    const handleDragOver = useCallback(
      (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        onDragStateChange(true);
      },
      [onDragStateChange],
    );

    const handleDragLeave = useCallback(
      (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        onDragStateChange(false);
      },
      [onDragStateChange],
    );

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        await handleFileSelect(file);
      }
      // Reset input value to allow re-selecting the same file
      e.target.value = '';
    };

    const handleAreaClick = useCallback(() => {
      if (!loading && ref && typeof ref !== 'function') {
        ref.current?.click();
      }
    }, [loading, ref]);

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleAreaClick();
        }
      },
      [handleAreaClick],
    );

    return (
      <StyledUploadFileBox
        dragActive={dragActive}
        hasFile={!!selectedFile}
        error={!!error}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={handleAreaClick}
        tabIndex={0}
        role="button"
        aria-label="Upload CSV file"
        onKeyDown={handleKeyDown}>
        <input
          type="file"
          accept={UPLOAD_CONFIG.ACCEPTED_FILE_TYPES.join(',')}
          ref={ref}
          style={{ display: 'none' }}
          onChange={handleFileChange}
          disabled={loading}
          aria-hidden="true"
        />

        {selectedFile ? (
          <Box sx={{ textAlign: 'center', width: '100%' }}>
            <CheckCircleIcon
              sx={{
                fontSize: 48,
                color: 'success.main',
                mb: 2,
              }}
            />
            <Typography variant="h6" gutterBottom>
              {UPLOAD_TEXT.FILE_READY}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {selectedFile.name} ({formatFileSize(selectedFile.size)})
            </Typography>
          </Box>
        ) : (
          <Box sx={{ textAlign: 'center' }}>
            <CloudUploadIcon
              sx={{
                fontSize: 48,
                color: dragActive ? 'primary.main' : 'text.secondary',
                mb: 2,
                transition: 'color 0.3s ease',
              }}
            />
            <Typography variant="h6" gutterBottom>
              {dragActive ? UPLOAD_TEXT.DROP_HERE : UPLOAD_TEXT.UPLOAD_CSV}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {UPLOAD_TEXT.DRAG_DROP_TEXT}
            </Typography>
            <Box sx={{ mt: 2 }}>
              <Chip label={UPLOAD_TEXT.CSV_ONLY} size="small" variant="outlined" sx={{ mr: 1 }} />
              <Chip label={UPLOAD_TEXT.MAX_SIZE} size="small" variant="outlined" />
            </Box>
          </Box>
        )}
      </StyledUploadFileBox>
    );
  },
);

FileUploadArea.displayName = 'FileUploadArea';
